// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, groupBy, mergeMap, switchMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import * as Actions from "../actions"
import * as Slice from "../slice"
import * as TimelineActions from "../../timeline/actions"
import {
  ListTimesheetFieldOptionsRequest,
  ListTimesheetFieldsRequest,
  TimesheetFieldOption,
  UpdateTimesheetFieldOptionRequest,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type UpdateTimesheetFieldOptionRpc = (request: UpdateTimesheetFieldOptionRequest) => Observable<TimesheetFieldOption>

export const updateTimesheetFieldOption = (updateTimesheetFieldOptionRpc: UpdateTimesheetFieldOptionRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.updateTimesheetFieldOption.match),
      groupBy(action => action.payload.request.getId()),
      mergeMap(group$ =>
        group$.pipe(
          switchMap(action =>
            updateTimesheetFieldOptionRpc(action.payload.request).pipe(
              mergeMap((response) => of(Slice.updateTimesheetFieldOption({
                option: response.toObject(),
                timesheetFieldId: action.payload.timesheetFieldId,
              }))),
              catchError(e => handleError(e, action.payload)),
            )
          )
        )
      ),
    )

const handleError = (e: unknown, payload: Actions.UpdateTimesheetFieldOptionPayload): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat([
        TimelineActions.listTimesheetFields({
          request: new ListTimesheetFieldsRequest().setSiteId(payload.request.getSiteId()),
          includeOptions: true,
        }),
        showSnackBar({
          message: t({
            id: "msg_timesheet_field_or_option_not_found",
            message: "Timesheet field or option not found.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat([
        Actions.listTimesheetFieldOptions(
          new ListTimesheetFieldOptionsRequest()
            .setSiteId(payload.request.getSiteId())
            .setTimesheetFieldId(payload.timesheetFieldId)
        ),
        showSnackBar({
          message: t({
            id: "msg_update_timesheet_field_option_failed_precondition",
            message: "Failed to update timesheet field option. Duplicate label or value.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({
          id: "msg_update_timesheet_field_option_permission_denied",
          message: "Failed to update timesheet field option. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_update_timesheet_field_option_failed",
      message: "Failed to update timesheet field option. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const updateTimesheetFieldOptionRpc = (request: UpdateTimesheetFieldOptionRequest): Observable<TimesheetFieldOption> =>
  grpc(() => timesheetFieldClient.updateTimesheetFieldOption(request, null))

export default updateTimesheetFieldOption(updateTimesheetFieldOptionRpc)
